import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from "firebase/auth";
import { Link } from 'react-router-dom';
import { auth, googleProvider, appleProvider } from "../lib/firebaseConfig.ts";
import { getAuthErrorMessage, getBackendErrorMessage, logAuthError } from '../utils/authErrors';
import { BACKEND_URL } from '../config/api';
import { validatePassword } from '../utils/validation';
import { Card, CardContent, CardHeader } from './ui/card';
import { Label } from './ui/label';
import { PasswordInput } from './ui/password-input';
import { Input } from './ui/input';
import { Button } from './ui/button';
import Turnstile from './Turnstile';

const RESEND_COOLDOWN_SECONDS = 60; // 60 second cooldown between resends

const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null); // 'google' | 'apple' | null
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileErrorShown, setTurnstileErrorShown] = useState(false);
  const [verificationData, setVerificationData] = useState({
    email: '',
    otp: ''
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    middleName: '', // Honeypot field - should always be empty
    referralCode: '' // Optional affiliate referral code
  });
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  
  // MFA State
  const [showMFAChallenge, setShowMFAChallenge] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRecaptchaVerifier, setMfaRecaptchaVerifier] = useState(null);

  const validatePasswordField = useCallback(
    (value, { forceTouched = false } = {}) => {
      const result = validatePassword(value);
      if (forceTouched || passwordTouched || value) {
        setPasswordError(result.valid ? '' : result.errors[0]);
      } else {
        setPasswordError('');
      }
      if (forceTouched) {
        setPasswordTouched(true);
      }
      return result;
    },
    [passwordTouched],
  );

  // Handle redirect result from social login (when coming back from Google/Apple)
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          setSocialLoading('redirect');
          const idToken = await result.user.getIdToken();
          
          const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ idToken }),
          });

          const loginResult = await response.json();

          if (response.ok) {
            toast.success('Signed in successfully!');
            onLogin(loginResult.user, loginResult.tokens);
          } else {
            toast.error(loginResult.message || 'Login failed');
          }
          setSocialLoading(null);
        }
      } catch (error) {
        if (error.code !== 'auth/popup-closed-by-user') {
          console.error('Redirect result error:', error);
        }
        setSocialLoading(null);
      }
    };

    handleRedirectResult();
  }, [onLogin]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'password') {
      setPasswordTouched(true);
      validatePasswordField(value);
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic password length check
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    // For signup, require Turnstile verification
    if (!isLogin && !turnstileToken) {
      toast.error('Security verification in progress. Please wait a moment and try again.');
      return;
    }
    
    setLoading(true);

    try {
      let endpoint, payload;
      
      if (isLogin) {
        // Login flow - Firebase authentication first
        try {
          const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
          const idToken = await userCredential.user.getIdToken();
          
          endpoint = '/auth/login';
          payload = { idToken };
        } catch (firebaseError) {
          // Check if MFA is required
          if (firebaseError.code === 'auth/multi-factor-auth-required') {
            // MFA is required - set up the challenge
            const resolver = getMultiFactorResolver(auth, firebaseError);
            setMfaResolver(resolver);
            
            // Initialize reCAPTCHA for phone verification
            const recaptchaVerifier = new RecaptchaVerifier(auth, 'mfa-recaptcha-container', {
              size: 'invisible',
              callback: () => {},
            });
            setMfaRecaptchaVerifier(recaptchaVerifier);
            
            // Get the phone hint (last 4 digits)
            const phoneHint = resolver.hints.find(h => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
            
            if (phoneHint) {
              // Send verification code to the phone
              const phoneInfoOptions = {
                multiFactorHint: phoneHint,
                session: resolver.session
              };
              const phoneAuthProvider = new PhoneAuthProvider(auth);
              const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
              setMfaVerificationId(verificationId);
              setShowMFAChallenge(true);
              toast.info(`Verification code sent to ••••${phoneHint.phoneNumber.slice(-4)}`);
            }
            
            setLoading(false);
            return;
          }
          
          // Handle other Firebase authentication errors
          const errorMessage = getAuthErrorMessage(firebaseError);
          logAuthError('Firebase Login', firebaseError, errorMessage);
          toast.error(errorMessage);
          setLoading(false);
          return;
        }
      } else {
        // Registration flow
        endpoint = '/auth/sign-up';
        
        // Check for affiliate referral code - prioritize form input, fallback to localStorage (from URL)
        const referralCodeFromStorage = localStorage.getItem('affiliateReferralCode');
        const referralCode = formData.referralCode?.trim()?.toUpperCase() || referralCodeFromStorage;
        
        payload = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          middleName: formData.middleName, // Honeypot field
          cfTurnstileToken: turnstileToken, // Cloudflare Turnstile token
          referralCode: referralCode || undefined, // Affiliate referral code (from form or URL)
        };
      }
      
      // Call backend API
      const response = await fetch(`${BACKEND_URL}/api${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        if (isLogin) {
          toast.success('Logged in successfully!');
          onLogin(result.user, result.tokens);
        } else {
          // Registration successful - go directly to dashboard (skip email verification)
          // Email verification is now optional and can be done later from Settings
          toast.success('Account created successfully! Welcome to Turnover.');
          if (result.emailSent === false) {
            toast.warning('Verification email could not be sent', {
              description: result.message,
            });
          }
          
          // Auto-login the user after registration
          try {
            const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
            const idToken = await userCredential.user.getIdToken();
            
            const loginResponse = await fetch(`${BACKEND_URL}/api/auth/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ idToken }),
            });
            
            const loginResult = await loginResponse.json();
            
            if (loginResponse.ok) {
              onLogin(loginResult.user, loginResult.tokens);
            } else {
              // Fallback: show verification step if auto-login fails
              toast.info('Please verify your email to continue.');
              setVerificationEmail(formData.email);
              setVerificationData({ email: formData.email, otp: '' });
              setShowEmailVerification(true);
            }
          } catch (loginError) {
            // Fallback: show verification step if auto-login fails
            console.error('Auto-login failed:', loginError);
            toast.info('Please verify your email to continue.');
            setVerificationEmail(formData.email);
            setVerificationData({ email: formData.email, otp: '' });
            setShowEmailVerification(true);
          }
        }
      } else {
        // Handle backend errors with proper messages
        const errorMessage = getBackendErrorMessage(result);
        logAuthError(isLogin ? 'Backend Login' : 'Backend Registration', result, errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      // Handle network or unexpected errors
      const errorMessage = getAuthErrorMessage(error);
      logAuthError(isLogin ? 'Login Network' : 'Registration Network', error, errorMessage);
      toast.error(errorMessage);
    }

    setLoading(false);
  };

  const handleEmailVerification = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(verificationData),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Email verified successfully! Logging you in...');
        const tokens = { accessToken: result.access_token, refreshToken: result.refresh_token || null };
        onLogin(result.user, tokens);
      } else {
        // Handle backend errors with proper messages
        const errorMessage = getBackendErrorMessage(result);
        logAuthError('Email Verification', result, errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      // Handle network errors
      const errorMessage = getAuthErrorMessage(error);
      logAuthError('Email Verification Network', error, errorMessage);
      toast.error(errorMessage);
    }

    setLoading(false);
  };

  // Handle MFA verification code submission
  const handleMFAVerification = async (e) => {
    e.preventDefault();
    
    if (!mfaCode || mfaCode.length !== 6) {
      toast.error('Please enter the complete 6-digit code from your authenticator app');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create credential from the verification code
      const cred = PhoneAuthProvider.credential(mfaVerificationId, mfaCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      
      // Complete the sign-in with MFA
      const userCredential = await mfaResolver.resolveSignIn(multiFactorAssertion);
      const idToken = await userCredential.user.getIdToken();
      
      // Call backend to complete login
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        toast.success('Logged in successfully!');
        onLogin(result.user, result.tokens);
        
        // Clean up MFA state
        setShowMFAChallenge(false);
        setMfaResolver(null);
        setMfaVerificationId(null);
        setMfaCode('');
      } else {
        const errorMessage = getBackendErrorMessage(result);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      
      if (error.code === 'auth/invalid-verification-code') {
        toast.error('Invalid code. Please check your authenticator app and try again.');
      } else {
        toast.error(error.message || 'Unable to verify code. Please try again.');
      }
    }
    
    setLoading(false);
  };

  const resendVerificationEmail = async () => {
    if (resendCooldown > 0) {
      toast.info(`Please wait ${resendCooldown} seconds before resending`);
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/resend-verification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: verificationEmail }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Verification email resent! Please check your inbox.');
        setResendCooldown(RESEND_COOLDOWN_SECONDS); // Start cooldown
      } else {
        const errorMessage = getBackendErrorMessage(result);
        logAuthError('Resend Verification', result, errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error);
      logAuthError('Resend Verification Network', error, errorMessage);
      toast.error(errorMessage);
    }
  };

  // Handle social login (Google/Apple)
  const handleSocialLogin = async (provider, providerName) => {
    setSocialLoading(providerName);
    
    try {
      // Try popup first (works better on desktop)
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      
      // Call backend to login/register the user
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      const loginResult = await response.json();

      if (response.ok) {
        toast.success(`Signed in with ${providerName} successfully!`);
        onLogin(loginResult.user, loginResult.tokens);
      } else {
        const errorMessage = getBackendErrorMessage(loginResult);
        logAuthError(`${providerName} Backend Login`, loginResult, errorMessage);
        toast.error(errorMessage);
      }
      setSocialLoading(null);
    } catch (error) {
      // Handle popup closed by user
      if (error.code === 'auth/popup-closed-by-user') {
        setSocialLoading(null);
        return;
      }
      
      // Handle MFA required for social login
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        
        // Initialize reCAPTCHA for phone verification
        const recaptchaVerifier = new RecaptchaVerifier(auth, 'mfa-recaptcha-container', {
          size: 'invisible',
          callback: () => {},
        });
        setMfaRecaptchaVerifier(recaptchaVerifier);
        
        // Get the phone hint
        const phoneHint = resolver.hints.find(h => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
        
        if (phoneHint) {
          try {
            const phoneInfoOptions = {
              multiFactorHint: phoneHint,
              session: resolver.session
            };
            const phoneAuthProvider = new PhoneAuthProvider(auth);
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
            setMfaVerificationId(verificationId);
            setShowMFAChallenge(true);
            toast.info(`Verification code sent to ••••${phoneHint.phoneNumber.slice(-4)}`);
          } catch (mfaError) {
            console.error('MFA verification error:', mfaError);
            toast.error('Unable to send verification code. Please check your phone number and try again.');
          }
        }
        
        setSocialLoading(null);
        return;
      }
      
      // If popup blocked or COOP issue, fall back to redirect
      if (error.code === 'auth/popup-blocked' || 
          error.code === 'auth/cancelled-popup-request' ||
          error.message?.includes('Cross-Origin-Opener-Policy')) {
        toast.info(`Redirecting to ${providerName} sign-in...`);
        // Use redirect instead - the result will be handled by useEffect
        await signInWithRedirect(auth, provider);
        return;
      }
      
      const errorMessage = getAuthErrorMessage(error);
      logAuthError(`${providerName} Login`, error, errorMessage);
      toast.error(errorMessage);
      setSocialLoading(null);
    }
  };

  const handleGoogleLogin = () => handleSocialLogin(googleProvider, 'Google');
  const _handleAppleLogin = () => handleSocialLogin(appleProvider, 'Apple');

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      password: '',
      middleName: '',
      referralCode: ''
    });
    setPasswordError('');
    setPasswordTouched(false);
    setTurnstileToken(null); // Reset Turnstile token when switching modes
    setTurnstileErrorShown(false); // Reset error state when switching modes
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 py-8">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-32 w-80 h-80 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-32 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md mx-auto"
      >
        <Card className="w-full shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
          {/* Back to Landing Page Link */}
          <div className="px-6 pt-4">
            <a 
              href="https://www.turnover-manager.com" 
              className="inline-flex items-center text-sm text-gray-500 hover:text-indigo-600 transition-colors"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Home
            </a>
          </div>
          
          <CardHeader className="text-center pb-2 px-6 pt-4">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <img src="/assets/images/logo_icon.png" alt="Turnover" className="mx-auto w-16 h-16 rounded-2xl shadow-lg mb-4" />
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Turnover
              </h1>
              <p className="text-base text-gray-600 mt-2">Fleet Management Made Simple</p>
            </motion.div>
          </CardHeader>

          <CardContent className="space-y-6 px-6 pb-8">
            {/* Hidden reCAPTCHA container for MFA */}
            <div id="mfa-recaptcha-container"></div>
            
            {showMFAChallenge ? (
              // MFA Verification Form
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                  <i className="fas fa-shield-alt text-indigo-600 text-2xl"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h2>
                <p className="text-base text-gray-600">
                  Enter the verification code sent to your phone
                </p>
                
                <form onSubmit={handleMFAVerification} className="space-y-4">
                  <div>
                    <Label htmlFor="mfaCode" className="text-sm font-medium text-gray-700">Verification Code</Label>
                    <Input
                      id="mfaCode"
                      name="mfaCode"
                      type="text"
                      required
                      placeholder="Enter 6-digit code"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                      className="mt-1 h-11 text-center text-lg tracking-widest"
                      maxLength="6"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={loading || mfaCode.length !== 6}
                    className="w-full h-11 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Verifying...
                      </span>
                    ) : (
                      'Verify & Sign In'
                    )}
                  </Button>
                </form>
                
                <button
                  onClick={() => {
                    setShowMFAChallenge(false);
                    setMfaResolver(null);
                    setMfaVerificationId(null);
                    setMfaCode('');
                    if (mfaRecaptchaVerifier) {
                      mfaRecaptchaVerifier.clear();
                      setMfaRecaptchaVerifier(null);
                    }
                  }}
                    className="text-sm text-gray-500 hover:text-indigo-600 transition-colors"
                >
                  ← Back to login
                </button>
              </div>
            ) : showEmailVerification ? (
              // Email Verification Form
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <i className="fas fa-envelope text-green-600 text-2xl"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Verify Your Email</h2>
                <p className="text-base text-gray-600">
                  We've sent a 6-digit verification code to<br />
                  <span className="font-medium text-gray-900 break-words">{verificationEmail}</span>
                </p>
                
                <form onSubmit={handleEmailVerification} className="space-y-4">
                  <div>
                    <Label htmlFor="otp" className="text-sm font-medium text-gray-700">Verification Code</Label>
                    <Input
                      id="otp"
                      name="otp"
                      type="text"
                      required
                      placeholder="Enter 6-digit code"
                      value={verificationData.otp}
                      onChange={(e) => setVerificationData({ ...verificationData, otp: e.target.value })}
                      className="mt-1 h-11 text-center text-lg tracking-widest"
                      maxLength="6"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  >
                    {loading ? 'Verifying...' : 'Verify Email'}
                  </Button>
                </form>
                
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Didn't receive the email?{' '}
                    <button 
                      onClick={resendVerificationEmail}
                      disabled={resendCooldown > 0}
                      className={`font-medium ${resendCooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:text-indigo-700'}`}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </button>
                  </p>
                  <button 
                    onClick={() => {setShowEmailVerification(false); setIsLogin(false);}}
                    className="text-sm text-gray-500 hover:text-gray-700 mt-2"
                  >
                    ← Back to registration
                  </button>
                </div>
              </div>
            ) : (
              // Login/Register Form
              <>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    data-testid="login-tab"
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      isLogin
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() => setIsLogin(true)}
                  >
                    Sign In
                  </button>
                  <button
                    data-testid="register-tab"
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      !isLogin
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() => setIsLogin(false)}
                  >
                    Sign Up
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-4"
                    >
                      <div>
                        <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">First Name</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          required={!isLogin}
                          placeholder="Enter your first name"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="mt-1 h-11"
                          data-testid="firstName-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">Last Name</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          required={!isLogin}
                          placeholder="Enter your last name"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="mt-1 h-11"
                          data-testid="lastName-input"
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* Honeypot field - hidden from users but visible to bots */}
                  {/* Bots will auto-fill this, real users won't see it */}
                  <div 
                    style={{ 
                      position: 'absolute', 
                      left: '-9999px',
                      opacity: 0,
                      height: 0,
                      overflow: 'hidden',
                      tabIndex: -1,
                    }}
                    aria-hidden="true"
                  >
                    <Label htmlFor="middleName">Middle Name</Label>
                    <Input
                      id="middleName"
                      name="middleName"
                      type="text"
                      autoComplete="off"
                      tabIndex={-1}
                      value={formData.middleName}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 h-11"
                      data-testid="email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                      Password <span className="text-xs text-gray-500 ml-1">(minimum 6 characters)</span>
                    </Label>
                    <PasswordInput
                      id="password"
                      name="password"
                      required
                      maxLength={128}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onBlur={() => validatePasswordField(formData.password, { forceTouched: true })}
                      className="mt-1 h-11"
                      data-testid="password-input"
                    />
                    {passwordError && (
                      <p className="text-xs text-red-500 mt-1">
                        {passwordError}
                      </p>
                    )}
                  </div>

                  {/* Referral Code - only show on signup */}
                  {!isLogin && (
                    <div>
                      {!showReferralInput ? (
                        <button
                          type="button"
                          onClick={() => setShowReferralInput(true)}
                          className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline"
                        >
                          Have a referral code?
                        </button>
                      ) : (
                        <div>
                          <Label htmlFor="referralCode" className="text-sm font-medium text-gray-700">
                            Referral Code <span className="text-xs text-gray-500">(optional)</span>
                          </Label>
                          <Input
                            id="referralCode"
                            name="referralCode"
                            type="text"
                            placeholder="Enter referral code"
                            value={formData.referralCode}
                            onChange={handleInputChange}
                            className="mt-1 h-11 uppercase"
                            maxLength={20}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Cloudflare Turnstile - invisible bot protection (only for signup) */}
                  {!isLogin && (
                    <Turnstile
                      onVerify={(token) => {
                        setTurnstileToken(token);
                        setTurnstileErrorShown(false); // Reset error state on successful verification
                      }}
                      onError={() => {
                        setTurnstileToken(null);
                        // Only show error toast once to prevent spam
                        if (!turnstileErrorShown) {
                          setTurnstileErrorShown(true);
                          toast.error('Security check failed. Retrying automatically...', {
                            duration: 3000,
                          });
                        }
                      }}
                      onExpire={() => setTurnstileToken(null)}
                    />
                  )}

                  {/* Show verification status for signup */}
                  {!isLogin && !turnstileToken && formData.password && (
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                      <div className="w-3 h-3 border-2 border-gray-400 border-t-indigo-600 rounded-full animate-spin"></div>
                      <span>Verifying you're human...</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || !formData.password || Boolean(passwordError) || (!isLogin && !turnstileToken)}
                    className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Processing...
                      </div>
                    ) : (
                      isLogin ? 'Sign In' : 'Create Account'
                    )}
                  </Button>
                  {isLogin && (
                    <div className="text-right">
                      <Link to="/forgot-password" className="text-sm text-indigo-600 hover:underline">
                        Forgot password?
                      </Link>
                    </div>
                  )}
                </form>

                {/* Social Login Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">or continue with</span>
                  </div>
                </div>

                {/* Social Login Buttons */}
                <div className="space-y-3">
                  <Button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={loading || socialLoading}
                    variant="outline"
                    className="w-full h-12 border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-3"
                    data-testid="google-login-button"
                  >
                    {socialLoading === 'Google' ? (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                    )}
                    Continue with Google
                  </Button>

                  {/* Apple Sign-In - Temporarily hidden while Apple Developer account is being set up */}
                  {/* TODO: Uncomment when Apple Developer account is ready
                  <Button
                    type="button"
                    onClick={handleAppleLogin}
                    disabled={loading || socialLoading}
                    variant="outline"
                    className="w-full h-12 border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-3"
                    data-testid="apple-login-button"
                  >
                    {socialLoading === 'Apple' ? (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                    )}
                    Continue with Apple
                  </Button>
                  */}
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="text-sm text-gray-600 hover:text-indigo-600 transition-colors"
                    data-testid="toggle-mode-button"
                  >
                    {isLogin 
                      ? "Don't have an account? Sign up" 
                      : "Already have an account? Sign in"
                    }
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Login;
